/**
 * Small helpers shared by the WhatsApp hub components — formatting, avatar
 * colours, delivery-state icons and the two substitution steps
 * (#tag# → guest value, {{n}} → template variable).
 */

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #34d399, #059669)',
    'linear-gradient(135deg, #818cf8, #4f46e5)',
    'linear-gradient(135deg, #f472b6, #db2777)',
    'linear-gradient(135deg, #fbbf24, #d97706)',
    'linear-gradient(135deg, #38bdf8, #0284c7)',
    'linear-gradient(135deg, #94a3b8, #64748b)',
];

export function initials(name) {
    if (!name) return '?';
    return String(name).trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
}

export function avatarStyle(name) {
    let hash = 0;
    const str = String(name || '');
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) % 997;
    }
    return {background: AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length]};
}

export function fullName(row) {
    if (row == null) return '';
    if (row.customerName) return row.customerName;
    if (row.name) return row.name;
    return [row.firstname, row.lastname].filter(Boolean).join(' ').trim();
}

/** Twilio lifecycle → icon + tone, the way WhatsApp itself shows ticks. */
export function statusMeta(status) {
    switch ((status || '').toLowerCase()) {
        case 'read':
            return {icon: 'fas fa-check-double', tone: 'read', key: 'read'};
        case 'delivered':
            return {icon: 'fas fa-check-double', tone: 'ok', key: 'delivered'};
        case 'sent':
        case 'success':
            return {icon: 'fas fa-check', tone: 'ok', key: 'sent'};
        case 'queued':
        case 'accepted':
        case 'scheduled':
        case 'sending':
            return {icon: 'far fa-clock', tone: 'muted', key: 'queued'};
        case 'failed':
        case 'undelivered':
            return {icon: 'fas fa-triangle-exclamation', tone: 'bad', key: 'failed'};
        case 'received':
            return {icon: 'fas fa-arrow-down', tone: 'in', key: 'received'};
        default:
            return {icon: 'fas fa-check', tone: 'muted', key: 'sent'};
    }
}

/** #firstname# → value; unknown tags are left in place (case-insensitive keys). */
export function replaceTags(text, tags) {
    if (text == null || text === '' || tags == null) return text || '';
    const lower = {};
    Object.keys(tags).forEach(k => lower[k.toLowerCase()] = tags[k]);
    return String(text).replace(/#([A-Za-z0-9_]+)#/g, (match, key) => {
        const value = lower[key.toLowerCase()];
        return value == null ? match : value;
    });
}

/** {{1}} → variables["1"]; missing variables stay visible so the user notices. */
export function renderVariables(text, variables) {
    if (text == null || text === '') return '';
    if (variables == null) return String(text);
    return String(text).replace(/\{\{\s*([A-Za-z0-9_]+)\s*}}/g, (match, key) => {
        const value = variables[key];
        return value == null || value === '' ? match : value;
    });
}

/** every {{n}} used in a template's texts, in order of first appearance */
export function collectVariables(template) {
    const keys = [];
    const texts = [template && template.body, template && template.title, template && template.subtitle,
        template && template.header, template && template.footer];
    texts.forEach(text => {
        if (!text) return;
        const re = /\{\{\s*([A-Za-z0-9_]+)\s*}}/g;
        let m;
        while ((m = re.exec(text)) != null) {
            if (!keys.includes(m[1])) keys.push(m[1]);
        }
    });
    if (template && template.variables) {
        Object.keys(template.variables).forEach(k => {
            if (!keys.includes(k)) keys.push(k);
        });
    }
    return keys;
}

export const TEMPLATE_TYPES = [
    {value: 'TEXT', twilio: 'twilio/text', icon: 'fas fa-align-left'},
    {value: 'MEDIA', twilio: 'twilio/media', icon: 'fas fa-image'},
    {value: 'QUICK_REPLY', twilio: 'twilio/quick-reply', icon: 'fas fa-reply'},
    {value: 'CALL_TO_ACTION', twilio: 'twilio/call-to-action', icon: 'fas fa-arrow-up-right-from-square'},
    {value: 'LIST_PICKER', twilio: 'twilio/list-picker', icon: 'fas fa-list-ul'},
    {value: 'CARD', twilio: 'twilio/card', icon: 'far fa-id-card'},
    {value: 'LOCATION', twilio: 'twilio/location', icon: 'fas fa-location-dot'},
    {value: 'AUTHENTICATION', twilio: 'whatsapp/authentication', icon: 'fas fa-key'},
];

/** twilio/quick-reply → QUICK_REPLY (builder key) — META and unknown types fall back to TEXT */
export function typeKey(twilioType) {
    const found = TEMPLATE_TYPES.find(t => t.twilio === twilioType);
    if (found) return found.value;
    if (twilioType === 'whatsapp/card') return 'CARD';
    if (twilioType === 'twilio/carousel') return 'CAROUSEL';
    if (twilioType === 'twilio/catalog') return 'CATALOG';
    if (twilioType === 'twilio/flows') return 'FLOWS';
    return 'TEXT';
}

export function approvalTone(status) {
    switch ((status || '').toLowerCase()) {
        case 'approved':
            return 'ok';
        case 'pending':
        case 'received':
            return 'wait';
        case 'rejected':
        case 'paused':
        case 'disabled':
            return 'bad';
        default:
            return 'muted';
    }
}

export const LANGUAGES = [
    {value: 'en', text: 'English'}, {value: 'de', text: 'Deutsch'}, {value: 'ru', text: 'Русский'},
    {value: 'es', text: 'Español'}, {value: 'fr', text: 'Français'}, {value: 'it', text: 'Italiano'},
    {value: 'pt_BR', text: 'Português'}, {value: 'nl', text: 'Nederlands'}, {value: 'pl', text: 'Polski'},
    {value: 'tr', text: 'Türkçe'}, {value: 'ar', text: 'العربية'}, {value: 'he', text: 'עברית'},
    {value: 'uk', text: 'Українська'}, {value: 'cs', text: 'Čeština'}, {value: 'hu', text: 'Magyar'},
];
